import { Button } from '@systemeio/components'
import { PlusIcon } from '@systemeio/icons'
import React, { useState, type JSX } from 'react'
import CreateModal, { CreateModalProps } from 'shared/components/create-modal'
import {
  FilterHeader,
  type FilterHeaderProps,
  type HeaderFilterGroup,
} from 'shared/components/filter-header'
import { HeaderCounter } from 'shared/components/header-counter'
import { useListingFilter } from 'shared/hooks/use-listing-filter'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { CountInterface, PaginationInterface } from 'shared/types/pagination-interface'

export type FilterableItemType<T> = {
  [K in keyof T]: {
    field: K
    onRender: (
      value: T[K],
      onChange: (value: T[K]) => void,
      state: T,
      onChangeState: (state: T) => void,
    ) => JSX.Element
    fields?: (keyof T)[]
    shouldRender?: boolean | ((state: T) => boolean)
  }
}[keyof T]

export type FiltererType<K> = {
  filter: K
  setFilter: (values: K) => void
  defaultFilters: K
}

export interface CreateDataInterface<T> extends Omit<
  CreateModalProps<T>,
  'afterLeave' | 'opened' | 'onClose'
> {
  onOpen?: () => Promise<void>
  testAttributePostfix?: string
  disabled?: boolean
  onAfterLeave?: () => void
}

export interface FilterDataInterface<K> {
  setPagination?: React.Dispatch<React.SetStateAction<PaginationInterface>>
  filterable: FilterableItemType<K>[]
  filterer: FiltererType<K>
  ready?: boolean
}

interface ListingLayoutBaseProps<R> {
  title: string | JSX.Element
  help?: React.ReactNode
  titleClassName?: string
  counter?: CountInterface | false
  children: JSX.Element
  actions?: React.ReactNode[]
  createData?: CreateDataInterface<R>
}

export type ListingLayoutProps<K, R> = ListingLayoutBaseProps<R> &
  (
    | { filters?: HeaderFilterGroup; filterData?: never }
    | { filters?: never; filterData: FilterDataInterface<K> }
  )

/** Split out so `useListingFilter` runs only where the descriptors exist. */
function ListingFilterHeader<K>({
  filterData,
  ...header
}: FilterHeaderProps & { filterData: FilterDataInterface<K> }) {
  const filters = useListingFilter(filterData)
  return <FilterHeader {...header} filters={filters} />
}

const ListingLayout = <K, R>(props: ListingLayoutProps<K, R>) => {
  const {
    title,
    help,
    titleClassName,
    counter,
    filterData,
    createData,
    children,
    actions,
    filters,
  } = props
  const { t } = useLocoTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isOpening, setIsOpening] = useState(false)

  const header: FilterHeaderProps = {
    title:
      typeof title === 'string' ? (
        <h1 className={titleClassName}>{title}</h1>
      ) : (
        <div className={titleClassName}>{title}</div>
      ),
    counter:
      'counter' in props && counter !== false ? <HeaderCounter count={counter?.count} /> : null,
    help,
    className: 'mb-5',
    actions: [
      ...(actions ?? []),
      createData && (
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={createData.disabled}
          isLoading={isOpening}
          data-test-id={createData.testAttributePostfix}
          onClick={async () => {
            setIsOpening(true)
            try {
              await createData.onOpen?.()
              setIsModalOpen(true)
            } finally {
              setIsOpening(false)
            }
          }}
        >
          <PlusIcon />
          <span>{t('global.create')}</span>
        </Button>
      ),
    ],
  }

  return (
    <>
      {filterData ? (
        <ListingFilterHeader {...header} filterData={filterData} />
      ) : (
        <FilterHeader {...header} filters={filters} />
      )}
      {children}
      {createData && (
        <CreateModal
          {...createData}
          opened={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          afterLeave={createData.onAfterLeave}
        />
      )}
    </>
  )
}

export default ListingLayout
